import "../../styles/dashboard/dashboard.css";

/*
  RecentTransactions
  ──────────────────
  Props:
    rawData            — flat array [ { transID, transactionDetails, customer, store, ... } ]
                         OR nested  { "date": [ { storeID, transactions:[...] } ] }
    selectedStore      — { storeId, storeName }
    currency           — string e.g. "₦" or "kr"
    onTransactionClick — fn(transaction)
*/

const capFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "—");

const DELIVERY_BADGE = {
  supplied: { cls: "db-badge--supplied", label: "Supplied" },
  pending: { cls: "db-badge--pending", label: "Pending" },
  notSupplied: { cls: "db-badge--notSupplied", label: "Not supplied" },
  returned: { cls: "db-badge--returned", label: "Returned" },
};

const PAYMENT_BADGE = {
  cash: { cls: "db-badge--cash", label: "Cash" },
  card: { cls: "db-badge--card", label: "Card" },
  mobilePay: { cls: "db-badge--mobile", label: "MobilePay" },
  POS: { cls: "db-badge--cash", label: "POS" },
  bankTransfer: { cls: "db-badge--bank", label: "Bank" },
};

export default function RecentTransactions({ rawData, selectedStore, currency = "₦", onTransactionClick }) {
  let transactions = [];

  if (rawData) {
    /* Flat array of transaction objects */
    if (Array.isArray(rawData)) {
      transactions = [...rawData];
      if (selectedStore?.storeId !== "1") {
        transactions = transactions.filter((t) => t.store?.id?.toString() === selectedStore?.storeId?.toString());
      }
    } else if (typeof rawData === "object") {
      /* Nested store structure */
      const allRecords = Object.values(rawData)[0] || [];
      const filtered = selectedStore?.storeId === "1" ? allRecords : allRecords.filter((r) => r.storeID?.toString() === selectedStore?.storeId?.toString());
      filtered?.forEach((store) => {
        if (store.transactions && Array.isArray(store.transactions)) {
          transactions.push(
            ...store.transactions.map((t) => ({
              ...t,
              store: { id: store.storeID, name: store.storeName || store.storeID },
            }))
          );
        }
      });
    }
  }

  transactions.sort((a, b) => new Date(b.transactionDetails?.date || b.createdAt || 0) - new Date(a.transactionDetails?.date || a.createdAt || 0));

  const rows = transactions.slice(0, 10);
  const shortId = (t) => (t.transID || t._id || "").slice(-10);
  const fmtAmt = (v) => `${currency}${(v || 0).toLocaleString()}`;

  const handleClick = (txn) => onTransactionClick && onTransactionClick(txn);

  if (rows.length === 0) {
    return (
      <div className='db-card'>
        <div className='db-card-hd'>
          <h4>Recent transactions</h4>
          <span className='db-txn-count'>0 total</span>
        </div>
        <p className='db-empty-msg'>No transactions found for this period</p>
      </div>
    );
  }

  return (
    <div className='db-card'>
      <div className='db-card-hd'>
        <h4> Transactions (for selected period) </h4>
        <span className='db-txn-count'>{transactions.length} total</span>
      </div>

      {/* ── Desktop table ── */}
      <div className='db-tbl-wrap'>
        <table className='db-txn-table'>
          <colgroup>
            <col className='col-id' />
            <col className='col-cust' />
            <col className='col-store' />
            <col className='col-pay' />
            <col className='col-del' />
            <col className='col-items' />
            <col className='col-amt' />
          </colgroup>
          <thead>
            <tr>
              <th>Trans ID</th>
              <th>Customer</th>
              <th>Store</th>
              <th>Payment</th>
              <th>Delivery</th>
              <th>Items</th>
              <th style={{ textAlign: "right" }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((txn, i) => {
              const d = txn.transactionDetails || {};
              const payment = PAYMENT_BADGE[d.paymentMode] || { cls: "db-badge--cash", label: capFirst(d.paymentMode) };
              const delivery = DELIVERY_BADGE[d.deliveryStatus] || { cls: "db-badge--pending", label: capFirst(d.deliveryStatus) };
              return (
                <tr key={txn._id || i} className='db-txn-row' onClick={() => handleClick(txn)} title='Click to view details'>
                  <td className='db-txn-ref'>#{shortId(txn)}</td>
                  <td>{txn.customer?.fullName || "—"}</td>
                  <td className='db-txn-store'>{txn.store?.name || "—"}</td>
                  <td>
                    <span className={`db-badge ${payment.cls}`}>{payment.label}</span>
                  </td>
                  <td>
                    <span className={`db-badge ${delivery.cls}`}>{delivery.label}</span>
                  </td>
                  <td className='db-txn-meta'>{d.numberOfItems || 0}</td>
                  <td className='db-txn-amt'>{fmtAmt(d.totalAmount)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Mobile card list (hidden on desktop via CSS) ── */}
      <div className='db-txn-cards'>
        {rows.map((txn, i) => {
          const d = txn.transactionDetails || {};
          const payment = PAYMENT_BADGE[d.paymentMode] || { cls: "db-badge--cash", label: capFirst(d.paymentMode) };
          const delivery = DELIVERY_BADGE[d.deliveryStatus] || { cls: "db-badge--pending", label: capFirst(d.deliveryStatus) };
          return (
            <div key={txn._id || i} className='db-txn-card' onClick={() => handleClick(txn)}>
              {/* Row 1: name + amount */}
              <div className='db-txn-card-row1'>
                <span className='db-txn-card-name'>{txn.customer?.fullName || "—"}</span>
                <span className='db-txn-card-amt'>{fmtAmt(d.totalAmount)}</span>
              </div>
              {/* Row 2: id · store · badges · items */}
              <div className='db-txn-card-row2'>
                <span className='db-txn-card-id'>#{shortId(txn)}</span>
                <span className='db-txn-card-sep'>·</span>
                <span className='db-txn-card-store'>{txn.store?.name || "—"}</span>
                <span className='db-txn-card-sep'>·</span>
                <span className={`db-badge ${payment.cls}`}>{payment.label}</span>
                <span className={`db-badge ${delivery.cls}`}>{delivery.label}</span>
                <span className='db-txn-card-sep'>·</span>
                <span className='db-txn-card-items'>{d.numberOfItems || 0} items</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
